import last from 'lodash/last';

import { colors, fontSizeSmall } from '@atlaskit/theme';

import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import type {
	TooltipFormatter,
	Count,
	LineChartByEnvTooltipFormatterParams,
} from '../../../../common/types.tsx';
import messages from './messages.tsx';

type CountByEnvironment = {
	developmentCount: Count | null;
	testingCount: Count | null;
	stagingCount: Count | null;
	productionCount: Count | null;
	unmappedCount: Count | null;
};
const getCountsByEnvironment = (
	series: LineChartByEnvTooltipFormatterParams[],
): CountByEnvironment => {
	const developmentSeries = series.find(
		(chartSeries: LineChartByEnvTooltipFormatterParams) =>
			chartSeries.data.customInfo.environment === 'development' && chartSeries.data.value !== null,
	);
	const testingSeries = series.find(
		(chartSeries: LineChartByEnvTooltipFormatterParams) =>
			chartSeries.data.customInfo.environment === 'testing' && chartSeries.data.value !== null,
	);
	const stagingSeries = series.find(
		(chartSeries: LineChartByEnvTooltipFormatterParams) =>
			chartSeries.data.customInfo.environment === 'staging' && chartSeries.data.value !== null,
	);
	const productionSeries = series.find(
		(chartSeries: LineChartByEnvTooltipFormatterParams) =>
			chartSeries.data.customInfo.environment === 'production' && chartSeries.data.value !== null,
	);
	const unmappedSeries = series.find(
		(chartSeries: LineChartByEnvTooltipFormatterParams) =>
			chartSeries.data.customInfo.environment === 'unmapped' && chartSeries.data.value !== null,
	);

	return {
		developmentCount: developmentSeries ? developmentSeries.data.value : null,
		testingCount: testingSeries ? testingSeries.data.value : null,
		stagingCount: stagingSeries ? stagingSeries.data.value : null,
		productionCount: productionSeries ? productionSeries.data.value : null,
		unmappedCount: unmappedSeries ? unmappedSeries.data.value : null,
	};
};

export const useTooltipFormatter = (): TooltipFormatter<LineChartByEnvTooltipFormatterParams[]> => {
	const { formatDate, formatMessage } = useIntl();

	return (series: LineChartByEnvTooltipFormatterParams[]) => {
		const weekStartDateText = formatMessage(messages.weekStartDate, {
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			date: formatDate(last(series).data.customInfo.startDateInclusive, {
				day: 'numeric',
				month: 'short',
			}),
		});

		const { developmentCount, testingCount, stagingCount, productionCount, unmappedCount } =
			getCountsByEnvironment(series);

		if (
			developmentCount === null &&
			testingCount === null &&
			stagingCount === null &&
			productionCount === null &&
			unmappedCount === null
		) {
			return null;
		}

		let resultHTML = `<div style="${container}"><div style="${headerWeekStartText}">${weekStartDateText}</div>`;
		if (developmentCount !== null) {
			const message = formatMessage(messages.numDevelopmentDeployments, {
				numDeployments: developmentCount,
			});
			resultHTML += `<p style="${countRow}">${message}</p>`;
		}
		if (testingCount !== null) {
			const message = formatMessage(messages.numTestingDeployments, {
				numDeployments: testingCount,
			});
			resultHTML += `<p style="${countRow}">${message}</p>`;
		}
		if (stagingCount !== null) {
			const message = formatMessage(messages.numStagingDeployments, {
				numDeployments: stagingCount,
			});
			resultHTML += `<p style="${countRow}">${message}</p>`;
		}
		if (productionCount !== null) {
			const message = formatMessage(messages.numProductionDeployments, {
				numDeployments: productionCount,
			});
			resultHTML += `<p style="${countRow}">${message}</p>`;
		}
		if (unmappedCount !== null) {
			const message = formatMessage(messages.numUnmappedDeployments, {
				numDeployments: unmappedCount,
			});
			resultHTML += `<p style="${countRow}">${message}</p>`;
		}

		resultHTML += '</div>';

		return resultHTML;
	};
};

const container = `
    padding: 0 ${gridSize / 2}px;
    background-color: ${colors.N900};
    border-radius: ${borderRadius};
    font-size: ${fontSizeSmall()}px;
`;

const headerWeekStartText = `
    height: ${gridSize * 2}px;
    margin-bottom: ${gridSize}px;
    text-decoration-line: underline;
    color: ${colors.N0};
    line-height: ${gridSize * 2}px;
`;

const countRow = `
    margin: 0px;
    padding: 0px;
`;
